<template>
  <div class="min-h-screen bg-gray-900 flex items-center justify-center p-4 relative overflow-hidden font-sans">
    <!-- Animated Background -->
    <div class="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
      <div class="blob bg-purple-600 opacity-40 top-0 -left-20 w-96 h-96 rounded-full mix-blend-multiply filter blur-3xl min-w-[20rem] min-h-[20rem] animate-blob"></div>
      <div class="blob bg-indigo-600 opacity-40 top-0 -right-20 w-96 h-96 rounded-full mix-blend-multiply filter blur-3xl min-w-[20rem] min-h-[20rem] animate-blob animation-delay-2000"></div>
      <div class="blob bg-pink-600 opacity-40 -bottom-32 left-20 w-96 h-96 rounded-full mix-blend-multiply filter blur-3xl min-w-[20rem] min-h-[20rem] animate-blob animation-delay-4000"></div>
    </div>

    <!-- Chat Container (Glassmorphism) -->
    <div class="relative z-10 w-full max-w-2xl bg-white bg-opacity-10 backdrop-filter backdrop-blur-lg border border-white border-opacity-20 rounded-3xl shadow-2xl flex flex-col h-[80vh] overflow-hidden">
      
      <!-- Header -->
      <div class="p-6 border-b border-white border-opacity-10 flex items-center justify-between">
        <div class="flex items-center space-x-4">
          <div class="relative">
            <div class="w-12 h-12 rounded-full bg-gradient-to-tr from-marbel-aqua to-purple-500 p-0.5">
               <div class="w-full h-full bg-gray-900 rounded-full flex items-center justify-center text-xl">
                 🤖
               </div>
            </div>
            <span class="absolute bottom-0 right-0 w-3 h-3 bg-green-400 border-2 border-gray-900 rounded-full"></span>
          </div>
          <div>
            <h2 class="text-white font-bold text-lg tracking-wide">Marbel Assistant</h2>
            <p class="text-xs text-gray-300 uppercase tracking-widest">AI Support Agent</p>
          </div>
        </div>
        <button class="text-white opacity-50 hover:opacity-100 transition-opacity">
           <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
        </button>
      </div>

      <!-- Chat Area -->
      <div class="flex-1 overflow-y-auto p-6 space-y-4" ref="chatArea">
        <transition-group name="message" tag="div" class="space-y-4">
          <div v-for="(msg, index) in messages" :key="'msg-' + index"
               :class="['flex w-full', msg.isUser ? 'justify-end' : 'justify-start']">
            
            <div :class="['max-w-[80%] rounded-2xl p-4 shadow-lg backdrop-filter backdrop-blur-sm bg-opacity-90 transition-all duration-300 transform', 
                          msg.isUser ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-br-none' : 'bg-gray-800 text-gray-100 rounded-bl-none border border-gray-700']">
              <p class="text-sm leading-relaxed">{{ msg.text }}</p>
              <p class="text-[10px] mt-2 opacity-60 text-right">{{ msg.time }}</p>
            </div>

          </div>
        </transition-group>

        <!-- Typing Indicator -->
        <div v-if="isTyping" class="flex justify-start animate-pulse">
           <div class="bg-gray-800 rounded-2xl rounded-bl-none p-4 border border-gray-700 shadow-lg flex space-x-1 items-center h-12">
             <div class="w-2 h-2 bg-gray-500 rounded-full animate-bounce"></div>
             <div class="w-2 h-2 bg-gray-500 rounded-full animate-bounce animation-delay-75"></div>
             <div class="w-2 h-2 bg-gray-500 rounded-full animate-bounce animation-delay-150"></div>
           </div>
        </div>
      </div>

      <!-- Input Area -->
      <div class="p-4 bg-black bg-opacity-20 border-t border-white border-opacity-10">
        <form @submit.prevent="sendMessage" class="flex items-center space-x-3">
          <input 
            v-model="newMessage"
            type="text" 
            placeholder="Escribe tu mensaje aquí..." 
            class="flex-1 bg-gray-900 bg-opacity-50 border border-gray-600 rounded-full px-6 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-marbel-aqua focus:ring-1 focus:ring-marbel-aqua transition-all backdrop-blur-sm"
          >
          <button 
            type="submit" 
            :disabled="!newMessage.trim()"
            class="p-3 bg-gradient-to-r from-marbel-aqua to-marbel-aqua-dark rounded-full text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all transform disabled:opacity-50 disabled:cursor-not-allowed">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path></svg>
          </button>
        </form>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'MarbelAssistantChat',
  data() {
    return {
      newMessage: '',
      isTyping: false,
      messages: [
        { 
          text: '¡Hola! Soy el asistente virtual de Marbel. ¿En qué puedo ayudarte hoy?', 
          isUser: false, 
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) 
        }
      ]
    }
  },
  methods: {
    async sendMessage() {
      if (!this.newMessage.trim()) return;

      const text = this.newMessage;
      this.newMessage = '';
      
      // User message
      this.messages.push({
        text: text,
        isUser: true,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      });

      this.scrollToBottom();
      this.isTyping = true;

      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 1500));

      this.isTyping = false;
      
      const response = this.getBotResponse(text);
      
      this.messages.push({
        text: response,
        isUser: false,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      });
      
      this.scrollToBottom();
    },
    getBotResponse(input) {
      const lowerInput = input.toLowerCase();
      
      if (lowerInput.includes('hola') || lowerInput.includes('buenos días')) {
        return '¡Hola de nuevo! Espero que estés teniendo un excelente día en Marbel. 🌟';
      }
      if (lowerInput.includes('precio') || lowerInput.includes('costo')) {
        return 'Para temas de precios, te sugiero revisar el módulo de cotizaciones o contactar a un agente de ventas. 💰';
      }
      if (lowerInput.includes('ayuda') || lowerInput.includes('help')) {
        return 'Claro, dime qué necesitas. Puedo guiarte por el sistema o responder dudas básicas no existenciales.';
      }
      if (lowerInput.includes('pedido') || lowerInput.includes('orden')) {
        return 'Puedes rastrear tus órdenes en la sección de "Mis Pedidos". ¿Necesitas el enlace directo?';
      }
      if (lowerInput.includes('gracias')) {
        return '¡De nada! Aquí estoy para servirte. 😉';
      }

      const randomResponses = [
        'Interesante... cuéntame más.',
        'Entiendo. Voy a tomar nota de eso.',
        'Esa es una excelente pregunta. Aunque soy un bot básico, me esforzaré por aprender más.',
        '¿Podrías reformular eso? Aún estoy aprendiendo el lenguaje humano de Marbel.'
      ];
      
      return randomResponses[Math.floor(Math.random() * randomResponses.length)];
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const chatArea = this.$refs.chatArea;
        chatArea.scrollTop = chatArea.scrollHeight;
      });
    }
  }
}
</script>

<style scoped>
/* Custom animations */
@keyframes blob {
  0% { transform: translate(0px, 0px) scale(1); }
  33% { transform: translate(30px, -50px) scale(1.1); }
  66% { transform: translate(-20px, 20px) scale(0.9); }
  100% { transform: translate(0px, 0px) scale(1); }
}

.animate-blob {
  animation: blob 10s infinite;
}

.animation-delay-2000 {
  animation-delay: 2s;
}

.animation-delay-4000 {
  animation-delay: 4s;
}

.animation-delay-75 {
  animation-delay: 75ms;
}

.animation-delay-150 {
  animation-delay: 150ms;
}

/* Scrollbar styling */
::-webkit-scrollbar {
  width: 6px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}
::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Vue transitions for messages */
.message-enter-active, .message-leave-active {
  transition: all 0.3s ease;
}
.message-enter-from, .message-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
